import type { FeedbackQuestionResponse } from '@lms/api-client/backend';
import { Label } from '@lms/ui/label';
import { type FormEvent, useState } from 'react';
import { useTranslations } from 'use-intl';
import { SimpleSelect } from '@/components/simple-select';
import { SubmitButton } from '@/components/submit-button';
import { addQuestionToSurveySchema } from '@/features/survey/actions.schema';
import { addQuestionToSurveyAction } from '@/features/survey/survey-admin-api';

// Ports features/survey/components/add-question-to-survey.tsx. The Next conform
// form (with a hidden `survey` field and a server action) becomes a controlled
// form; `surveyId` is passed as a prop and the caller revalidates via `onAdded`
// in place of `revalidatePath`/`redirect`.
export default function AddQuestionToSurveyForm({
  surveyId,
  questions,
  onAdded,
}: {
  surveyId: number;
  questions: FeedbackQuestionResponse[];
  onAdded: () => void;
}) {
  const t = useTranslations('survey');
  const [question, setQuestion] = useState('');
  const [error, setError] = useState<string | undefined>(undefined);
  const [pending, setPending] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(undefined);

    const submission = addQuestionToSurveySchema.safeParse({
      question: Number.parseInt(question, 10),
    });
    if (!submission.success) {
      setError(t('question'));
      return;
    }

    setPending(true);
    try {
      const result = await addQuestionToSurveyAction(
        surveyId,
        submission.data.question,
      );
      if (result.ok) {
        setQuestion('');
        onAdded();
      } else {
        setError(result.error);
      }
    } finally {
      setPending(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <Label>{t('question')}</Label>
        <SimpleSelect
          value={question}
          onValueChange={setQuestion}
          placeholder={t('question')}
          options={questions.map((q) => ({
            value: `${q.id}`,
            label: q.description,
          }))}
        />
      </div>
      {error && <div className="text-destructive text-sm">{error}</div>}
      <SubmitButton
        label={t('add_question')}
        pending={pending}
        disabled={!question}
      />
    </form>
  );
}
